import classNames from "classnames";
import React, { useState } from "react";
import styles from "./tooltipComponent.css";

export interface Props {
  positionPreset?: PresetType;
  text?: any;
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  tooltipShow?: boolean;
  style?: StyleType;
  changeTransformTooltip?: string;
  changeTransformOutset?: string;
  breakWords?: boolean;
  displayInline?: boolean;
  autoHide?: boolean;
  autoHideTime?: number;
}

export type TooltipComponentProps = React.PropsWithChildren<Props>;

type PresetType = "left" | "right" | "top" | "bottom" | "top right" | "top left" | "bottom right";
type StyleType = "widget" | "dashboard" | "error";
type TooltipDisplay = "none" | "block" | "inline";

export const TooltipComponent = ({
  positionPreset,
  text,
  width,
  maxWidth,
  minWidth,
  tooltipShow = true,
  style,
  changeTransformTooltip,
  changeTransformOutset,
  breakWords,
  displayInline,
  children,
  autoHide,
  autoHideTime,
}: TooltipComponentProps) => {
  autoHide ??= true;
  autoHideTime ??= 4000;

  const [tooltipTimeout, setTooltipTimeout] = useState<ReturnType<typeof setTimeout>>();
  const [tooltipDisplay, setTooltipDisplay] = useState<TooltipDisplay>("none");

  const getStyleForPosition = (): string => {
    switch (positionPreset) {
      case "left":
        return styles.left;
      case "right":
        return styles.right;
      case "top":
        return styles.top;
      case "bottom":
        return styles.bottom;
      case "top right":
        return styles["top-right"];
      case "bottom right":
        return styles["bottom-right"];
      case "top left":
        return styles["top-left"];
      default:
        return styles.left;
    }
  };

  const getStyleForPreset = (): string => {
    switch (style) {
      case "widget":
        return styles.widget;
      case "error":
        return styles.error;
      case "dashboard":
        return styles.dashboard;
      default:
        return styles.dashboard;
    }
  };

  const clearDisplayingTimeout = (): void => {
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(undefined);
    }
  };

  const setDisplayingTimeout = (): void => {
    clearDisplayingTimeout();
    if (autoHide) {
      const timeout = setTimeout(() => {
        hideOnMouseLeave();
      }, autoHideTime);

      setTooltipTimeout(timeout);
    }
  };

  const displayOnMouseEnter = (): void => {
    setTooltipDisplay("block");
    setDisplayingTimeout();
  };

  const hideOnMouseLeave = (): void => {
    clearDisplayingTimeout();
    setTooltipDisplay("none");
  };

  const render = () => {
    const positionSettings = getStyleForPosition();
    const styleSettings = getStyleForPreset();
    const wrapperClass = style === "error" ? styles.errorWrapper : "";

    return (
      <div
        className={classNames(styles.tooltipWrapper, wrapperClass, {
          [styles["inline-wrapper"]]: displayInline
        })}
        onMouseEnter={displayOnMouseEnter}
        onMouseLeave={hideOnMouseLeave}
      >
        {children}
        {tooltipShow && (
          <div
            className={classNames(styles.tooltip, positionSettings, styleSettings)}
            style={{
              display: tooltipDisplay,
              maxWidth,
              width,
              transform: changeTransformTooltip,
              minWidth
            }}
          >
            <div className={classNames({ [styles["break-words"]]: breakWords })}>{text}</div>
            <div className={styles.outset} style={{ transform: changeTransformOutset }} />
          </div>
        )}
      </div>
    );
  };

  return render();
};
